<template>
  <div class="login-container">
    <!-- 动态背景 -->
    <div class="background-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <el-form
      ref="resetForm"
      :model="resetForm"
      :rules="resetRules"
      class="login-form"
      auto-complete="on"
      label-position="top"
      v-loading="loading"
      element-loading-text="正在重置密码..."
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(255, 255, 255, 0.8)"
    >
      <div class="title-container">
        <div class="logo-icon">🔐</div>
        <h3 class="title">重置密码</h3>
        <p class="subtitle">通过邮箱验证码重置您的密码</p>
      </div>

      <el-form-item prop="email" label="邮箱" class="input-item">
        <span class="svg-container">
          <svg-icon icon-class="email" />
        </span>
        <el-input
          ref="email"
          v-model="resetForm.email"
          placeholder="请输入绑定的邮箱地址"
          name="email"
          type="email"
          tabindex="1"
          auto-complete="on"
        />
      </el-form-item>

      <div class="code-row">
        <el-form-item prop="code" label="验证码" class="code-item input-item">
          <span class="svg-container">
            <svg-icon icon-class="code" />
          </span>
          <el-input
            ref="code"
            v-model="resetForm.code"
            placeholder="请输入验证码"
            name="code"
            type="text"
            tabindex="2"
            auto-complete="off"
          />
        </el-form-item>
        <el-button
          :disabled="codeDisabled || countDown > 0"
          type="primary"
          class="send-code-btn"
          @click="sendCode"
        >
          {{ countDown > 0 ? `${countDown}秒后重试` : '发送验证码' }}
        </el-button>
      </div>

      <el-form-item prop="newPassword" label="新密码" class="input-item">
        <span class="svg-container">
          <svg-icon icon-class="password" />
        </span>
        <el-input
          :key="passwordType"
          ref="newPassword"
          v-model="resetForm.newPassword"
          :type="passwordType"
          placeholder="请输入新密码（至少6位）"
          name="newPassword"
          tabindex="3"
          auto-complete="off"
        />
        <span class="show-pwd" @click="showPwd">
          <svg-icon :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'" />
        </span>
      </el-form-item>

      <el-form-item prop="confirmPassword" label="确认密码" class="input-item">
        <span class="svg-container">
          <svg-icon icon-class="password" />
        </span>
        <el-input
          :key="confirmPasswordType"
          ref="confirmPassword"
          v-model="resetForm.confirmPassword"
          :type="confirmPasswordType"
          placeholder="请再次输入新密码"
          name="confirmPassword"
          tabindex="4"
          auto-complete="off"
          @keyup.enter.native="handleReset"
        />
        <span class="show-pwd" @click="showConfirmPwd">
          <svg-icon :icon-class="confirmPasswordType === 'password' ? 'eye' : 'eye-open'" />
        </span>
      </el-form-item>

      <el-button
        :loading="loading"
        type="primary"
        style="width: 100%; margin-bottom: 24px; margin-top: 8px;"
        @click.native.prevent="handleReset"
      >重置密码</el-button>

      <div class="login-link-container">
        <span class="login-text">想起密码了？</span>
        <router-link class="login-link" to="/login">返回登录</router-link>
      </div>

    </el-form>
  </div>
</template>

<script>
import { sendResetPasswordCode, resetPasswordByEmail } from '@/api/user'
import { Message } from 'element-ui'
import { Encrypt } from '@/utils/Secret'

export default {
  name: 'ForgotPassword',
  data() {
    const validateEmail = (rule, value, callback) => {
      const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!value) {
        callback(new Error('请输入邮箱地址'))
      } else if (!emailReg.test(value)) {
        callback(new Error('请输入正确的邮箱格式'))
      } else {
        callback()
      }
    }
    const validateCode = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入验证码'))
      } else {
        callback()
      }
    }
    const validatePassword = (rule, value, callback) => {
      if (!value || value.length < 6) {
        callback(new Error('密码不能少于6位'))
      } else {
        callback()
      }
    }
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入密码'))
      } else if (value !== this.resetForm.newPassword) {
        callback(new Error('两次输入密码不一致'))
      } else {
        callback()
      }
    }
    return {
      resetForm: {
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: ''
      },
      resetRules: {
        email: [{ required: true, trigger: 'blur', validator: validateEmail }],
        code: [{ required: true, trigger: 'blur', validator: validateCode }],
        newPassword: [{ required: true, trigger: 'blur', validator: validatePassword }],
        confirmPassword: [{ required: true, trigger: 'blur', validator: validateConfirmPassword }]
      },
      loading: false,
      passwordType: 'password',
      confirmPasswordType: 'password',
      codeDisabled: false,
      countDown: 0,
      timer: null
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    showPwd() {
      this.passwordType = this.passwordType === 'password' ? '' : 'password'
      this.$nextTick(() => {
        this.$refs.newPassword.focus()
      })
    },
    showConfirmPwd() {
      this.confirmPasswordType = this.confirmPasswordType === 'password' ? '' : 'password'
      this.$nextTick(() => {
        this.$refs.confirmPassword.focus()
      })
    },
    sendCode() {
      // 验证邮箱格式
      this.$refs.resetForm.validateField('email', (errorMsg) => {
        if (!errorMsg) {
          this.codeDisabled = true
          sendResetPasswordCode(this.resetForm.email)
            .then((res) => {
              if (res.code) {
                Message({ message: '验证码已发送到您的邮箱，请查收', type: 'success', duration: 5 * 1000 })
                // 开始倒计时
                this.countDown = 60
                this.timer = setInterval(() => {
                  this.countDown--
                  if (this.countDown <= 0) {
                    clearInterval(this.timer)
                    this.codeDisabled = false
                  }
                }, 1000)
              } else {
                Message({ message: res.msg || '发送失败，请稍后重试', type: 'error', duration: 5 * 1000 })
                this.codeDisabled = false
              }
            })
            .catch((err) => {
              console.error('发送验证码失败:', err)
              Message({ message: '发送失败，请检查邮箱地址是否正确', type: 'error', duration: 5 * 1000 })
              this.codeDisabled = false
            })
        } else {
          Message({ message: '请先输入正确的邮箱地址', type: 'warning', duration: 3 * 1000 })
        }
      })
    },
    handleReset() {
      this.$refs.resetForm.validate((valid) => {
        if (valid) {
          this.loading = true
          // 加密密码后发送
          resetPasswordByEmail(
            this.resetForm.email,
            this.resetForm.code,
            Encrypt(this.resetForm.newPassword)
          )
            .then((res) => {
              if (res.code) {
                Message({ message: '密码重置成功，请使用新密码登录', type: 'success', duration: 5 * 1000 })
                // 跳转到登录页
                setTimeout(() => {
                  this.$router.push({ path: '/login' })
                }, 2000)
              } else {
                Message({ message: res.msg || '密码重置失败，请重试', type: 'error', duration: 5 * 1000 })
              }
              this.loading = false
            })
            .catch((err) => {
              console.error('密码重置失败:', err)
              Message({ message: '密码重置失败，请重试', type: 'error', duration: 5 * 1000 })
              this.loading = false
            })
        } else {
          Message({ message: '请填写完整的重置信息', type: 'warning', duration: 5 * 1000 })
          return false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$bg: #0f172a;
$bg_light: #1e293b;
$light_gray: #f1f5f9;
$dark_gray: #94a3b8;
$primary: #3b82f6;
$primary_hover: #2563eb;
$surface: rgba(255, 255, 255, 0.08);
$border: rgba(255, 255, 255, 0.1);
$surface_hover: rgba(255, 255, 255, 0.12);
$accent: #8b5cf6;
$success: #10b981;

.login-container {
  min-height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  // 动态背景装饰
  .background-decoration {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0;

    .circle {
      position: absolute;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      animation: float 20s infinite ease-in-out;
    }

    .circle-1 {
      width: 500px;
      height: 500px;
      top: -250px;
      left: -250px;
      animation-delay: 0s;
    }

    .circle-2 {
      width: 400px;
      height: 400px;
      bottom: -200px;
      right: -200px;
      animation-delay: 7s;
    }

    .circle-3 {
      width: 300px;
      height: 300px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation-delay: 14s;
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(50px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-30px, 30px) scale(0.9);
    }
  }

  // 表单布局
  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }
  ::v-deep .el-form--label-top .el-form-item__label {
    color: #334155;
    font-weight: 600;
    font-size: 14px;
    padding: 0 0 8px 2px;
    line-height: 1.2;
  }

  // 输入框样式
  .input-item {
    ::v-deep .el-input__inner {
      background: #ffffff;
      border: 2px solid #e2e8f0;
      color: #334155;
      border-radius: 12px;
      padding: 12px 15px;
      padding-left: 45px;
      font-size: 15px;
      font-weight: 500;
      transition: all 0.3s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      &:hover {
        border-color: #94a3b8;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      }

      &:focus {
        border-color: #667eea;
        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        outline: none;
        background: #ffffff;
      }
    }
  }

  // placeholder 样式
  .input-item {
    ::v-deep .el-input__inner::placeholder { color: #94a3b8; }
    ::v-deep .el-input__inner::-webkit-input-placeholder { color: #94a3b8; }
    ::v-deep .el-input__inner::-moz-placeholder { color: #94a3b8; }
    ::v-deep .el-input__inner:-ms-input-placeholder { color: #94a3b8; }
    ::v-deep .el-input__inner::-ms-input-placeholder { color: #94a3b8; }
  }

  .login-form {
    position: relative;
    width: 480px;
    max-width: 90%;
    padding: 48px 50px;
    margin: 0 auto;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.1),
      0 0 0 1px rgba(255, 255, 255, 0.2) inset;
    overflow: visible;
    z-index: 1;
    animation: slideUp 0.6s ease-out;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .el-form-item {
    position: relative;
  }

  .svg-container {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    pointer-events: none;
  }

  .title-container {
    position: relative;
    text-align: center;
    margin-bottom: 32px;

    .logo-icon {
      font-size: 48px;
      margin-bottom: 16px;
      animation: bounce 2s infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-10px);
      }
    }

    .title {
      font-size: 32px;
      color: #1e293b;
      margin: 0 auto 8px;
      font-weight: 700;
      letter-spacing: -0.5px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .subtitle {
      font-size: 14px;
      color: #64748b;
      margin: 0;
      font-weight: 400;
    }
  }

  .show-pwd {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    color: #64748b;
    cursor: pointer;
    user-select: none;
    z-index: 2;
    transition: all 0.3s ease;

    &:hover {
      color: #667eea;
    }
  }

  // 验证码输入行样式
  .code-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 18px;

    .code-item {
      flex: 1;
      margin-bottom: 0;
    }

    .send-code-btn {
      margin-top: 30px;
      min-width: 130px;
      height: 46px;
      font-size: 14px;
      font-weight: 600;
      white-space: nowrap;
      border-radius: 12px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      border: none;
      color: white;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }

  // 主按钮美化
  ::v-deep .el-button--primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: #ffffff;
    font-weight: 600;
    font-size: 16px;
    padding: 14px 0;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;

    &:hover, &:focus {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
      background: linear-gradient(135deg, #5568d3 0%, #6a3e8f 100%);
    }

    &:active {
      transform: translateY(0);
    }

    &.is-disabled, &.is-loading {
      opacity: 0.6;
      transform: none;
      box-shadow: none;
    }
  }

  // 错误提示样式
  ::v-deep .el-form-item__error {
    color: #ef4444;
    padding-left: 15px;
    font-size: 12px;
    margin-top: 6px;
    font-weight: 500;
    line-height: 1.4;
  }

  // 登录链接样式
  .login-link-container {
    text-align: center;
    padding: 16px 0 8px;

    .login-text {
      color: #64748b;
      font-size: 14px;
      margin-right: 8px;
    }

    .login-link {
      color: #667eea;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      position: relative;
      transition: all 0.3s ease;

      &:after {
        content: '';
        position: absolute;
        width: 0;
        height: 2px;
        bottom: -2px;
        left: 0;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        transition: width 0.3s ease;
      }

      &:hover {
        color: #5568d3;

        &:after {
          width: 100%;
        }
      }
    }
  }
}
</style>
